// Public-website visitor analytics client (logiselect.com.au).
// Opt-out model: tracking runs by default; user can reject via the consent
// banner. DNT and Sec-GPC are always honoured.

// Analytics/lead endpoints are always called same-origin so the browser never
// triggers a CORS preflight (the public website's nginx proxies /api/ to the
// same backend as the TMS host). VITE_ANALYTICS_BASE_URL can override this
// for local dev where the frontend runs on a different port than the backend.
const ANALYTICS_ORIGIN = String(
  (import.meta as any).env?.VITE_ANALYTICS_BASE_URL || ''
).replace(/\/$/, '')
const API_BASE = `${ANALYTICS_ORIGIN}/api/v1/analytics`

const SID_COOKIE = 'ls_sid'
const CONSENT_COOKIE = 'ls_consent'   // 'accepted' | 'rejected' | absent = unknown
const SID_TTL_DAYS = 180
const CONSENT_TTL_DAYS = 365

type EventItem = {
  eventId: string
  eventType: string
  path?: string
  referrer?: string
  payload?: Record<string, unknown>
}

const OUTBOX_KEY = 'ls_analytics_outbox'
const MAX_OUTBOX_EVENTS = 500
const RETRY_DELAY_MS = 10_000

function loadOutbox(): EventItem[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(OUTBOX_KEY) || '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter(item => item && typeof item.eventId === 'string' && typeof item.eventType === 'string')
      .slice(-MAX_OUTBOX_EVENTS)
  } catch { return [] }
}

function persistOutbox() {
  try { localStorage.setItem(OUTBOX_KEY, JSON.stringify(queue.slice(-MAX_OUTBOX_EVENTS))) } catch { /* best effort */ }
}

let queue: EventItem[] = loadOutbox()
let flushTimer: ReturnType<typeof setTimeout> | null = null
let sessionRetryTimer: ReturnType<typeof setTimeout> | null = null
let flushInProgress = false
let sessionStarted = false
let visibleSince = document.visibilityState === 'visible' ? performance.now() : null
let visibleTimeMs = 0

/* ------------------------------ debug tracer ------------------------------ */
// Activated by ?ls_debug=1 in the URL (persisted per tab in sessionStorage)
// or by calling window.__lsAnalyticsDebug.enable() in DevTools. When active,
// every step of the accept/reject/track/flush pipeline is logged with request
// URLs, bodies, and response status so it's obvious why (or why not) rows
// land in the analytics.* tables.

type DbgEntry = { t: number; msg: string; data?: unknown }
const DBG_KEY = 'ls_debug'
const DBG_BUFFER: DbgEntry[] = []
const DBG_MAX = 500

function dbgEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false
    if (new URL(location.href).searchParams.get('ls_debug') === '1') {
      sessionStorage.setItem(DBG_KEY, '1')
    }
    return sessionStorage.getItem(DBG_KEY) === '1'
  } catch { return false }
}

function dbg(msg: string, data?: unknown) {
  if (!dbgEnabled()) return
  const entry: DbgEntry = { t: Date.now(), msg, data }
  DBG_BUFFER.push(entry)
  if (DBG_BUFFER.length > DBG_MAX) DBG_BUFFER.shift()
  // eslint-disable-next-line no-console
  console.log('[ls-analytics]', msg, data ?? '')
  renderDebugOverlay(entry)
}

let overlayEl: HTMLDivElement | null = null
let overlayLog: HTMLPreElement | null = null
function renderDebugOverlay(entry: DbgEntry) {
  if (typeof document === 'undefined') return
  if (!overlayEl) {
    overlayEl = document.createElement('div')
    overlayEl.setAttribute('data-ls-debug', 'analytics')
    Object.assign(overlayEl.style, {
      position: 'fixed', right: '12px', bottom: '12px', width: 'min(520px, 92vw)',
      maxHeight: '40vh', zIndex: '2147483647',
      background: 'rgba(17,24,39,0.94)', color: '#e5e7eb',
      font: '12px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace',
      border: '1px solid #374151', borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0,0,0,.35)', display: 'flex',
      flexDirection: 'column', overflow: 'hidden',
    } as CSSStyleDeclaration)
    const header = document.createElement('div')
    Object.assign(header.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 10px', background: '#111827', borderBottom: '1px solid #374151',
    } as CSSStyleDeclaration)
    header.innerHTML = '<span style="font-weight:600">analytics debug</span>'
    const btns = document.createElement('div')
    const mk = (label: string, onClick: () => void) => {
      const b = document.createElement('button')
      b.textContent = label
      Object.assign(b.style, {
        marginLeft: '6px', background: '#374151', color: '#f9fafb',
        border: '0', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer',
        font: 'inherit',
      } as CSSStyleDeclaration)
      b.onclick = onClick
      return b
    }
    btns.appendChild(mk('copy', () => {
      try { navigator.clipboard.writeText(JSON.stringify(DBG_BUFFER, null, 2)) } catch { /* */ }
    }))
    btns.appendChild(mk('clear', () => {
      DBG_BUFFER.length = 0
      if (overlayLog) overlayLog.textContent = ''
    }))
    btns.appendChild(mk('close', () => {
      try { sessionStorage.removeItem(DBG_KEY) } catch { /* */ }
      overlayEl?.remove(); overlayEl = null; overlayLog = null
    }))
    header.appendChild(btns)
    overlayLog = document.createElement('pre')
    Object.assign(overlayLog.style, {
      margin: '0', padding: '8px 10px', overflow: 'auto', flex: '1 1 auto',
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    } as CSSStyleDeclaration)
    overlayEl.appendChild(header)
    overlayEl.appendChild(overlayLog)
    document.body.appendChild(overlayEl)
  }
  const time = new Date(entry.t).toISOString().substring(11, 23)
  const line = `${time}  ${entry.msg}${entry.data === undefined ? '' : '  ' + safeStringify(entry.data)}\n`
  overlayLog!.textContent += line
  overlayLog!.scrollTop = overlayLog!.scrollHeight
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v) } catch { return String(v) }
}

if (typeof window !== 'undefined') {
  ;(window as any).__lsAnalyticsDebug = {
    enable() { try { sessionStorage.setItem(DBG_KEY, '1') } catch { /* */ }
      dbg('debug:enabled', { origin: location.origin, apiBase: API_BASE }) },
    disable() { try { sessionStorage.removeItem(DBG_KEY) } catch { /* */ }
      overlayEl?.remove(); overlayEl = null; overlayLog = null },
    status() {
      return {
        enabled: dbgEnabled(),
        apiBase: API_BASE,
        consent: getConsent(),
        sessionId: readCookie(SID_COOKIE),
        sessionStarted,
        queued: queue.length,
        trackingDisabled: trackingDisabled(),
        dnt: (navigator as any).doNotTrack === '1',
        gpc: (navigator as any).globalPrivacyControl === true,
      }
    },
    dump() { return DBG_BUFFER.slice() },
    async ping() {
      dbg('debug:ping -> /session')
      await postSession(getConsent())
    },
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Expires=${expires}; SameSite=Lax${secure}`
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
    return (crypto as any).randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function getConsent(): 'accepted' | 'rejected' | 'unknown' {
  const v = readCookie(CONSENT_COOKIE)
  return v === 'accepted' || v === 'rejected' ? v : 'unknown'
}

export function setConsent(state: 'accepted' | 'rejected') {
  dbg('setConsent:enter', { state, prior: getConsent(), sessionStarted, sid: readCookie(SID_COOKIE) })
  writeCookie(CONSENT_COOKIE, state, CONSENT_TTL_DAYS)
  dbg('setConsent:cookieWritten', { cookie: CONSENT_COOKIE, value: state, ttlDays: CONSENT_TTL_DAYS })
  if (state === 'rejected') {
    // Best-effort erasure of any server-side rows for this session
    // BEFORE we delete the sid cookie (otherwise we have nothing to send).
    const sid = readCookie(SID_COOKIE)
    if (sid) {
      const ok = navigator.sendBeacon?.(`${API_BASE}/erase`,
        new Blob([JSON.stringify({ sessionId: sid })], { type: 'application/json' }))
      dbg('setConsent:erase:beacon', { url: `${API_BASE}/erase`, sid, queued: ok })
    }
    // Purge our own identifier and any queued events.
    document.cookie = `${SID_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
    queue = []
    persistOutbox()
    dbg('setConsent:rejected:cleanup', {})
    return
  }
  // Accepted: make sure analytics is running and register the visitor now.
  // startAnalytics is idempotent; if it already ran we still (re)post the
  // session so the backend records the updated consent state.
  if (!sessionStarted) {
    dbg('setConsent:accepted -> startAnalytics()')
    startAnalytics()
  } else {
    dbg('setConsent:accepted -> postSession(accepted) (already started)')
    void postSession('accepted')
  }
}

function trackingDisabled(): boolean {
  if (getConsent() === 'rejected') return true
  const dnt = (navigator as any).doNotTrack === '1'
    || (navigator as any).msDoNotTrack === '1'
    || (window as any).doNotTrack === '1'
  const gpc = (navigator as any).globalPrivacyControl === true
  return dnt || gpc
}

function getOrCreateSessionId(): string {
  let sid = readCookie(SID_COOKIE)
  if (!sid) {
    sid = uuidv4()
    writeCookie(SID_COOKIE, sid, SID_TTL_DAYS)
  }
  return sid
}

function deviceType(): string {
  const ua = navigator.userAgent
  if (/bot|crawl|spider/i.test(ua)) return 'bot'
  if (/Tablet|iPad/i.test(ua)) return 'tablet'
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile'
  return 'desktop'
}

function parseUtm(): Record<string, string | undefined> {
  const u = new URL(location.href)
  return {
    utmSource:   u.searchParams.get('utm_source')   ?? undefined,
    utmMedium:   u.searchParams.get('utm_medium')   ?? undefined,
    utmCampaign: u.searchParams.get('utm_campaign') ?? undefined,
    utmTerm:     u.searchParams.get('utm_term')     ?? undefined,
    utmContent:  u.searchParams.get('utm_content')  ?? undefined,
  }
}

async function postSession(consentState: 'accepted' | 'rejected' | 'unknown') {
  const conn = (navigator as any).connection
  const body = {
    sessionId: getOrCreateSessionId(),
    deviceType: deviceType(),
    browser: navigator.userAgent,
    os: (navigator as any).userAgentData?.platform || navigator.platform || '',
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenW: window.screen.width,
    screenH: window.screen.height,
    viewportW: window.innerWidth,
    viewportH: window.innerHeight,
    connectionType: conn?.effectiveType || '',
    landingPath: location.pathname + location.search,
    landingReferrer: document.referrer,
    ...parseUtm(),
    consentState,
    clientMetadata: {
      title: document.title,
      devicePixelRatio: window.devicePixelRatio,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      touchPoints: navigator.maxTouchPoints,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemoryGb: (navigator as any).deviceMemory,
      cookieEnabled: navigator.cookieEnabled,
      online: navigator.onLine,
      vendor: navigator.vendor,
      platform: (navigator as any).userAgentData?.platform || navigator.platform,
      webdriver: navigator.webdriver,
    },
  }
  const url = `${API_BASE}/session`
  dbg('postSession:request', { url, consentState, sessionId: body.sessionId, landingPath: body.landingPath })
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    })
    let payload: unknown
    try { payload = await res.clone().json() } catch { payload = await res.clone().text().catch(() => undefined) }
    dbg('postSession:response', { status: res.status, ok: res.ok, contentType: res.headers.get('content-type'), payload })
    if (!res.ok || !String(res.headers.get('content-type') || '').includes('json')) {
      dbg('postSession:WARN non-JSON or error response — request likely did not reach the backend', {
        hint: 'Check nginx-website.conf proxy for /api/v1/analytics/ and reload nginx',
      })
      scheduleSessionRetry(consentState)
    } else if (sessionRetryTimer) {
      clearTimeout(sessionRetryTimer)
      sessionRetryTimer = null
    }
  } catch (e: any) {
    dbg('postSession:error', { message: e?.message || String(e) })
    scheduleSessionRetry(consentState)
    /* fail closed – analytics must never break the page */
  }
}

function scheduleSessionRetry(consentState: 'accepted' | 'rejected' | 'unknown') {
  if (sessionRetryTimer || trackingDisabled()) return
  sessionRetryTimer = setTimeout(() => {
    sessionRetryTimer = null
    void postSession(consentState)
  }, RETRY_DELAY_MS)
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(flush, 2000)
}

function scheduleRetry() {
  if (flushTimer || trackingDisabled()) return
  flushTimer = setTimeout(flush, RETRY_DELAY_MS)
}

async function flush() {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null }
  if (flushInProgress || queue.length === 0 || trackingDisabled()) {
    dbg('flush:skip', { queued: queue.length, trackingDisabled: trackingDisabled() })
    return
  }
  flushInProgress = true
  const eventsSnapshot = queue.slice(0, 100)
  const eventIds = new Set(eventsSnapshot.map(event => event.eventId))
  const payload = JSON.stringify({ sessionId: getOrCreateSessionId(), events: eventsSnapshot })
  const url = `${API_BASE}/event`
  dbg('flush:send', { url, count: eventsSnapshot.length, types: eventsSnapshot.map(e => e.eventType) })
  try {
    const res = await fetch(url, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      keepalive: true,
    })
    dbg('flush:response', { status: res.status, ok: res.ok })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    queue = queue.filter(event => !eventIds.has(event.eventId))
    persistOutbox()
    if (queue.length > 0) scheduleFlush()
  } catch (e: any) {
    dbg('flush:error', { message: e?.message || String(e), retained: eventsSnapshot.length })
    persistOutbox()
    scheduleRetry()
  } finally {
    flushInProgress = false
  }
}

export function track(eventType: string, payload: Record<string, unknown> = {}) {
  if (trackingDisabled()) {
    dbg('track:blocked', { eventType, reason: 'trackingDisabled' })
    return
  }
  queue.push({
    eventId: uuidv4(),
    eventType,
    path: location.pathname + location.search,
    referrer: document.referrer,
    payload,
  })
  if (queue.length > MAX_OUTBOX_EVENTS) queue = queue.slice(-MAX_OUTBOX_EVENTS)
  persistOutbox()
  dbg('track:queued', { eventType, queued: queue.length })
  scheduleFlush()
}

export function trackPageView() {
  track('page_view', { title: document.title })
}

export async function submitLead(input: {
  name: string; email?: string; company?: string; phone?: string; message?: string; website?: string
  consentAck: boolean
}): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...input,
        sessionId: readCookie(SID_COOKIE),
        sourcePath: location.pathname + location.search,
      }),
    })
    if (!res.ok) {
      const payload = await res.json().catch(() => null)
      return { ok: false, error: payload?.error || `HTTP ${res.status}` }
    }
    const j = await res.json()
    return { ok: true, id: j.id }
  } catch (e: any) {
    return { ok: false, error: e?.message || 'network error' }
  }
}

let scrollMarks = new Set<number>()
function onScroll() {
  const doc = document.documentElement
  const scrolled = doc.scrollTop + window.innerHeight
  const total = doc.scrollHeight
  if (total <= 0) return
  const pct = Math.floor((scrolled / total) * 100)
  for (const mark of [25, 50, 75, 100]) {
    if (pct >= mark && !scrollMarks.has(mark)) {
      scrollMarks.add(mark)
      track('scroll_depth', { pct: mark })
    }
  }
}

function onClick(ev: MouseEvent) {
  const target = ev.target as HTMLElement | null
  const actionable = target?.closest('a,button,[role="button"],input[type="submit"]') as HTMLElement | null
  if (actionable) {
    track('click', {
      tag: actionable.tagName.toLowerCase(),
      id: actionable.id || undefined,
      role: actionable.getAttribute('role') || undefined,
      name: actionable.getAttribute('name') || undefined,
      ariaLabel: actionable.getAttribute('aria-label') || undefined,
    })
  }
  const a = target?.closest('a')
  if (!a) return
  const href = a.getAttribute('href') || ''
  if (!href) return
  try {
    const url = new URL(href, location.href)
    if (url.host && url.host !== location.host) {
      track('outbound_click', { href: url.href, host: url.host })
    }
  } catch { /* ignore */ }
}

function updateVisibleTime() {
  if (visibleSince !== null) {
    visibleTimeMs += Math.max(0, performance.now() - visibleSince)
    visibleSince = performance.now()
  }
  return Math.round(visibleTimeMs)
}

function trackEngagement(reason: 'heartbeat' | 'hidden' | 'pagehide') {
  track('engagement', { visibleTimeMs: updateVisibleTime(), reason })
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    trackEngagement('hidden')
    visibleSince = null
  } else {
    visibleSince = performance.now()
    void flush()
  }
}

/** Start the analytics runtime. Idempotent; safe to call from React effects. */
export function startAnalytics() {
  if (sessionStarted) { dbg('startAnalytics:skip already started'); return }
  sessionStarted = true
  if (trackingDisabled()) { dbg('startAnalytics:skip trackingDisabled', { consent: getConsent() }); return }
  dbg('startAnalytics:begin', { consent: getConsent(), apiBase: API_BASE })
  void postSession(getConsent())
  trackPageView()
  scrollMarks = new Set()
  window.addEventListener('scroll', onScroll, { passive: true })
  document.addEventListener('click', onClick, { passive: true })
  window.addEventListener('online', flush)
  window.addEventListener('focus', flush)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.setInterval(() => {
    if (document.visibilityState === 'visible') trackEngagement('heartbeat')
  }, 15_000)
  window.addEventListener('pagehide', () => { trackEngagement('pagehide'); void flush() })
  window.addEventListener('beforeunload', flush)
  if (queue.length > 0) scheduleFlush()
}
